import * as Sentry from '@sentry/react';
import { Map } from 'immutable';

import { shapeSubscriptionsForApp, shapeUserDataForApp } from '../utils/user';
import { getFromLocalStorage, saveToLocalStorage } from '../utils/localStorage';
import amplitude from '../utils/tracking/amplitude';
import mixpanel from '../utils/tracking/mixpanel';
import api from '../utils/api';

import { EMPTY, MESSENGER_LAUNCHER_ID } from '../constants';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { VELA_THEME } from '../constants/theme';

import actions from '../actions';

function* changeTheme(reduction, { theme }) {
  yield function effect(dispatch) {
    const oldTheme = reduction.getIn(['user', VELA_THEME]);
    api.user
      .setProfileValue({ [VELA_THEME]: theme })
      .then(
        () => dispatch(actions.user.changeTheme.fulfilled({ theme })),
        (error) => dispatch(actions.user.changeTheme.rejected({ error, oldTheme })),
      );
  };

  return reduction
    .deleteIn(['user', 'loaded'])
    .setIn(['user', 'loading'], true)
    .setIn(['user', VELA_THEME], theme);
}

function* changeThemeFulfilled(reduction, { theme }) {
  yield function effect() {
    saveToLocalStorage({ [VELA_THEME]: theme });
  };

  return reduction
    .deleteIn(['user', 'loading'])
    .setIn(['user', 'loaded'], true);
}

function* changeThemeRejected(reduction, { error, oldTheme }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CHANGE_THEME,
      })
    );
  };

  return reduction
    .deleteIn(['user', 'loading'])
    .setIn(['user', 'loaded'], true)
    .setIn(['user', VELA_THEME], oldTheme);
}

function* getConfig(reduction) {
  yield function effect(dispatch) {
    api.user
      .getConfig()
      .then(
        (config) => dispatch(actions.user.getConfig.fulfilled(config)),
        (error) => dispatch(actions.user.getConfig.rejected(error)),
      );
  };

  return reduction;
}

function* getConfigFulfilled(reduction, config) {
  return reduction.setIn(['user', 'config'], Map(config));
}

function* getConfigRejected(reduction, error) {
  yield function effect() {
    console.error(error);
  };

  return reduction;
}

function* getSubscriptions(reduction) {
  yield function effect(dispatch) {
    api.billing.getSubscriptions()
      .then(
        (response) => dispatch(actions.user.getSubscriptions.fulfilled(response)),
        (error) => dispatch(actions.user.getSubscriptions.rejected(error)),
      );
  };

  return reduction;
}

function* getSubscriptionsFulfilled(reduction, response) {
  return reduction.setIn(['user', 'subscriptions'], shapeSubscriptionsForApp({ subscriptions: response }));
}

function* getSubscriptionsRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.ERROR,
        keep: true,
      })
    );
  };

  return reduction;
}

function* getUserData(reduction, payload = {}) {
  const { code, shopId } = payload;

  yield function effect(dispatch) {
    api.user
      .getUserData()
      .then(
        (data) => dispatch(actions.user.getUserData.fulfilled({ code, data, shopId })),
        (error) => dispatch(actions.user.getUserData.rejected(error))
      );
  };

  return reduction.setIn(['user', 'loading'], true);
}

function* getUserDataFulfilled(reduction, { code, data, shopId }) {
  if (!reduction.getIn(['user', 'loading'])) return reduction;

  Sentry.setTag('userId', data.userId);

  let user = shapeUserDataForApp({
    data,
    user: reduction
      .get('user', EMPTY.MAP)
      .delete('loading')
      .set('loaded', true)
      .set('loggedIn', true),
  });

  if (!user.get(VELA_THEME)) {
    const theme = reduction.getIn(['user', VELA_THEME]);
    user = user.set(VELA_THEME, theme);

    yield function effect() {
      api.user.setProfileValue({ [VELA_THEME]: theme });
    };
  }

  if (getFromLocalStorage({ [VELA_THEME]: undefined })[VELA_THEME] !== user.get(VELA_THEME)) {
    saveToLocalStorage({ [VELA_THEME]: user.get(VELA_THEME) });
  }

  yield function effect(dispatch) {
    if (code) {
      dispatch(actions.shops.validateOauthToken(code));
    }

    dispatch(actions.user.getConfig());
    dispatch(actions.user.getUserProfile());
    dispatch(actions.user.getSubscriptions());
    dispatch(
      actions.shops.getShops(
        shopId ||
        reduction.getIn(['shops', 'current']) ||
        user.getIn(['lastSeenShop', 'id'])
      )
    );
  };

  return reduction.deleteIn(['auth', 'loading']).set('user', user);
}

function* getUserDataRejected(reduction) {
  yield function effect(dispatch) {
    dispatch(actions.user.getConfig());
  };

  return reduction
    .deleteIn(['auth', 'loading'])
    .deleteIn(['user', 'loading'])
    .setIn(['user', 'loaded'], true)
    .setIn(['user', 'loggedIn'], false);
}

function* getUserProfile(reduction) {
  yield function effect(dispatch) {
    api.user
      .getJWTData()
      .then(
        (response) => dispatch(actions.user.getUserProfile.fulfilled(response)),
        (error) => dispatch(actions.user.getUserProfile.rejected(error)),
      );
  };

  return reduction;
}

function* getUserProfileFulfilled(reduction, data) {
  const { avatar, email, firstName, lastName, userHash, userId, userName } = data;

  if (email !== reduction.getIn(['user', 'profile', 'email'])) {
    yield function effect() {
      amplitude.setUserId(email);
      mixpanel.setDistinctId(email);

      if ('Intercom' in window) {
        window.Intercom(
          'update',
          {
            email: userName,
            user_id: userId,
            user_hash: userHash,
            custom_launcher_selector: `#${MESSENGER_LAUNCHER_ID}`,
            hide_default_launcher: true,
            alignment: 'left',
            horizontal_padding: 21,
            vertical_padding: 100,
          }
        );
      }
    };
  }

  return reduction.setIn(['user', 'profile'], Map({ avatar, email, firstName, lastName }));
}

function* getUserProfileRejected(reduction, error) {
  console.error(error);
  return reduction;
}

function* setProfileValue(reduction, { name, saveInDB = true, value }) {
  if (typeof value !== 'string') return reduction;

  if (saveInDB) {
    yield function effect() {
      api.user.setProfileValue({ [name]: value });
    };
  }

  return reduction.setIn(['user', name], value);
}

const user = {
  [actions.user.changeTheme]: changeTheme,
  [actions.user.changeTheme.fulfilled]: changeThemeFulfilled,
  [actions.user.changeTheme.rejected]: changeThemeRejected,
  [actions.user.getConfig]: getConfig,
  [actions.user.getConfig.fulfilled]: getConfigFulfilled,
  [actions.user.getConfig.rejected]: getConfigRejected,
  [actions.user.getSubscriptions]: getSubscriptions,
  [actions.user.getSubscriptions.fulfilled]: getSubscriptionsFulfilled,
  [actions.user.getSubscriptions.rejected]: getSubscriptionsRejected,
  [actions.user.getUserData]: getUserData,
  [actions.user.getUserData.fulfilled]: getUserDataFulfilled,
  [actions.user.getUserData.rejected]: getUserDataRejected,
  [actions.user.getUserProfile]: getUserProfile,
  [actions.user.getUserProfile.fulfilled]: getUserProfileFulfilled,
  [actions.user.getUserProfile.rejected]: getUserProfileRejected,
  [actions.user.setProfileValue]: setProfileValue,
};

export default user;
